import React, { useState, useEffect } from "react";
import { Button, Modal, Stack, Text, Group, ThemeIcon } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDownload,
  IconShare,
  IconSquarePlus,
  IconCheck,
} from "@tabler/icons-react";

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  // Check for iOS devices via userAgent
  const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
  // Check for iPad on iOS 13+ (reports as Mac but has touch)
  const isIPadOS =
    navigator.userAgent.includes("Mac") && navigator.maxTouchPoints > 1;
  return isIOSDevice || isIPadOS;
}

function isInstalledAsPWA(): boolean {
  if (typeof window === "undefined") return false;

  // Check if running in standalone mode (installed as PWA)
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

  // iOS Safari specific check
  const isIOSStandalone =
    (navigator as { standalone?: boolean }).standalone === true;

  return isStandalone || isIOSStandalone;
}

interface IOSInstallButtonProps {
  height?: number;
}

export const IOSInstallButton: React.FC<IOSInstallButtonProps> = ({
  height = 40,
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);

  useEffect(() => {
    const ios = isIOS();
    const installed = isInstalledAsPWA();

    setShouldShow(ios && !installed);
  }, []);

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <Button
        variant="light"
        size="compact-md"
        h={height}
        leftSection={<IconDownload size={18} />}
        onClick={open}
      >
        Installieren
      </Button>

      <Modal
        opened={opened}
        onClose={close}
        title="App installieren"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Installiere die App auf deinem Home-Screen für schnellen Zugriff.
          </Text>

          <Stack gap="xs">
            <Group gap="sm" align="flex-start">
              <ThemeIcon variant="light" size="lg" color="blue">
                <IconShare size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={500} size="sm">
                  1. App teilen
                </Text>
                <Text size="xs" c="dimmed">
                  Tippe unten in Safari auf die drei Punkte und wähle
                  &ldquo;Teilen&rdquo;
                </Text>
              </Stack>
            </Group>

            <Group gap="sm" align="flex-start">
              <ThemeIcon variant="light" size="lg" color="blue">
                <IconSquarePlus size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={500} size="sm">
                  2. &ldquo;Zum Home-Bildschirm&rdquo;
                </Text>
                <Text size="xs" c="dimmed">
                  Scrolle und wähle &ldquo;Zum Home-Bildschirm&rdquo;
                </Text>
              </Stack>
            </Group>

            <Group gap="sm" align="flex-start">
              <ThemeIcon variant="light" size="lg" color="blue">
                <IconCheck size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={500} size="sm">
                  3. Hinzufügen bestätigen
                </Text>
                <Text size="xs" c="dimmed">
                  Tippe oben rechts auf &ldquo;Hinzufügen&rdquo;
                </Text>
              </Stack>
            </Group>
          </Stack>

          <Button variant="light" onClick={close} mt="sm">
            Verstanden
          </Button>
        </Stack>
      </Modal>
    </>
  );
};
